import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { rxResource } from '@angular/core/rxjs-interop';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NZ_DRAWER_DATA, NzDrawerService } from 'ng-zorro-antd/drawer';
import { UserViewComponent } from '@beautyapp/users';
import {
  ServicePackageService,
  PriceAdjustment,
  ServicePackagePurchase,
  ServicePackagePurchaseServiceDetail,
  ServicePackageUsage,
  OPERATION_DETAIL_OPENER,
  PurchaseOperationRow,
} from '@beautyapp/core';
import {
  auditFieldLabelKey,
  formatAuditValue,
  formatAddedValue,
} from '../shared/audit-format';
import { EDIT_REASON_LABELS_KA } from '../purchase-edit-modal/edit-reason';

interface AuditEntry {
  id: string;
  fieldChanged: string;
  oldValue: unknown;
  newValue: unknown;
  editorId: string;
  editor?: { id: string; fullName?: string };
  editedAt: string;
}

type AuditRow = {
  kind: 'audit';
  date: string;
  payload: AuditEntry;
};

type AdjustmentRow = {
  kind: 'adjustment';
  date: string;
  payload: PriceAdjustment;
};

type UsageRow = {
  kind: 'usage';
  date: string;
  payload: ServicePackageUsage;
};

type PurchaseRow = {
  kind: 'purchase';
  date: string;
  payload: { soldBy: string | null; price: number };
};

export type TimelineRow = AuditRow | AdjustmentRow | UsageRow | PurchaseRow;

interface TimelineSources {
  audits: AuditEntry[];
  adjustments: PriceAdjustment[];
  detail: ServicePackagePurchase | null;
}

@Component({
  selector: 'beautyapp-package-purchase-drawer',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NzTableModule,
    NzTagModule,
    NzSpinModule,
    NzTabsModule,
  ],
  templateUrl: './package-purchase-drawer.component.html',
})
export class PackagePurchaseDrawerComponent {
  private packageService = inject(ServicePackageService);
  private drawerService = inject(NzDrawerService);
  private data = inject<{ purchaseId: string }>(NZ_DRAWER_DATA);

  readonly purchaseId = this.data.purchaseId;

  readonly resource = rxResource<TimelineSources, { id: string }>({
    params: () => ({ id: this.purchaseId }),
    stream: ({ params }) => {
      if (!params.id) {
        return of<TimelineSources>({
          audits: [],
          adjustments: [],
          detail: null,
        });
      }
      return forkJoin({
        audits: (
          this.packageService.getPurchaseAudit(params.id) as Observable<
            AuditEntry[]
          >
        ).pipe(catchError(() => of([] as AuditEntry[]))),
        adjustments: this.packageService
          .getPurchaseAdjustments(params.id)
          .pipe(catchError(() => of([] as PriceAdjustment[]))),
        detail: (
          this.packageService.getPurchaseById(
            params.id
          ) as Observable<ServicePackagePurchase | null>
        ).pipe(catchError(() => of(null))),
      });
    },
  });

  readonly detail = computed(() => this.resource.value()?.detail ?? null);

  readonly services = computed(
    () =>
      this.detail()?.services ?? ([] as ServicePackagePurchaseServiceDetail[])
  );

  readonly timeline = computed<TimelineRow[]>(() => {
    const v = this.resource.value();
    if (!v) return [];
    const rows: TimelineRow[] = [];
    for (const a of v.audits ?? []) {
      rows.push({ kind: 'audit', date: a.editedAt, payload: a });
    }
    for (const a of v.adjustments ?? []) {
      rows.push({ kind: 'adjustment', date: a.editedAt, payload: a });
    }
    for (const u of v.detail?.usages ?? []) {
      rows.push({ kind: 'usage', date: u.usedAt, payload: u });
    }
    // Synthesize the sale as the first (oldest) history event so a freshly-sold
    // package — which has no audits/adjustments/usages yet — isn't shown as
    // empty. Sorted oldest-last, this lands at the bottom of the timeline.
    const d = v.detail as
      | (ServicePackagePurchase & {
          purchasedAt?: string;
          createdBy?: { name?: string };
          purchasePrice?: number;
        })
      | null;
    if (d?.purchasedAt) {
      rows.push({
        kind: 'purchase',
        date: d.purchasedAt,
        payload: {
          soldBy: d.createdBy?.name ?? null,
          price: Number(d.purchasePrice ?? 0),
        },
      });
    }
    rows.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    return rows;
  });

  /**
   * Unit price actually charged: the BE-computed effectivePrice when present,
   * otherwise the no-discount fallback (discountedPrice > 0 ? discounted :
   * original). discountedPrice = 0 means "no discount", NOT free — binding it
   * directly collapsed no-discount lines to 0.00.
   */
  effectivePrice(s: ServicePackagePurchaseServiceDetail): number {
    if (typeof s.effectivePrice === 'number') return s.effectivePrice;
    const discounted = Number(s.discountedPrice);
    return discounted > 0 ? discounted : Number(s.originalPrice);
  }

  /** Line total actually charged: effective unit price × quantity (BE lineTotal when present). */
  lineTotal(s: ServicePackagePurchaseServiceDetail): number {
    if (typeof s.lineTotal === 'number') return s.lineTotal;
    return this.effectivePrice(s) * Number(s.totalQuantity);
  }

  /** Line total at the original (pre-discount) price × quantity. */
  originalLineTotal(s: ServicePackagePurchaseServiceDetail): number {
    return Number(s.originalPrice) * Number(s.totalQuantity);
  }

  /** Sum of each service's line total (effective unit price × quantity). */
  readonly servicesTotal = computed(() => {
    const detailTotal = this.detail()?.servicesTotal;
    if (typeof detailTotal === 'number') return detailTotal;
    return this.services().reduce(
      (sum, s) => sum + this.effectivePrice(s) * Number(s.totalQuantity),
      0
    );
  });

  // Template helpers from shared audit-format
  readonly fieldLabelKey = auditFieldLabelKey;
  readonly formatValue = (field: string, value: unknown): string =>
    formatAuditValue(
      field,
      value,
      this.services() as Array<{ serviceId: string; serviceName?: string }>
    );
  readonly formatAdded = (value: unknown): string =>
    formatAddedValue(
      value,
      this.services() as Array<{ serviceId: string; serviceName?: string }>
    );

  reasonLabel(reason: PriceAdjustment['reason']): string {
    return EDIT_REASON_LABELS_KA[reason] ?? reason;
  }

  private readonly operationOpener = inject(OPERATION_DETAIL_OPENER, {
    optional: true,
  });

  readonly operations = signal<PurchaseOperationRow[] | null>(null);
  readonly operationsLoading = signal(false);

  /** Lazy-load on first activation of the Operations tab. */
  onOperationsTab(): void {
    if (this.operations() !== null || this.operationsLoading()) return;
    this.operationsLoading.set(true);
    this.packageService
      .getPurchaseOperations(this.purchaseId)
      .pipe(catchError(() => of([] as PurchaseOperationRow[])))
      .subscribe((rows) => {
        this.operations.set(rows);
        this.operationsLoading.set(false);
      });
  }

  openOperation(operationId: number): void {
    this.operationOpener?.open(operationId);
  }

  /**
   * Open the shared user-profile drawer for a party on this purchase — the
   * owner (beneficiary/consumer) or, for a gift, the payer. Stacks on top of
   * this drawer; no-ops when the id is missing. Mirrors sell-package-modal's
   * viewUser() so the profile drawer looks/behaves identically everywhere.
   */
  openUser(id?: string | null, name?: string | null): void {
    if (!id) return;
    this.drawerService.create<UserViewComponent, { data: string }, string>({
      nzTitle: name ?? '',
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: { padding: '0 15px 15px 15px' },
      nzData: { data: id },
    });
  }
}
